import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCompanyInfo } from '../../hooks/useCompanyInfo';
import { isLaboSukePublicEnabled } from '../../utils/config';
import { gaisukePublicPath } from '../../utils/gaisukePath';
import { gaisukeFxPublicPath } from '../../utils/gaisukeFxPath';
import { laboSukePublicPath } from '../../utils/laboSukePath';

type FooterProps = {
  /** 計助ダッシュボード等: main 直下で余白を付けない（薄グレー帯＋高さ計算ズレの防止） */
  noTopMargin?: boolean;
  /** 1行だけの薄いフッタ（競馬アリーナの1画面UI用） */
  compact?: boolean;
};

export default function Footer({ noTopMargin, compact }: FooterProps) {
  const { t, i18n } = useTranslation();
  const { companyInfo } = useCompanyInfo();
  const laboSukePublic = isLaboSukePublicEnabled();
  const brand = companyInfo?.name || t('home.brand', { defaultValue: 'Kensuke' });

  if (compact) {
    return (
      <footer
        className={`bg-dark text-white py-1 flex-shrink-0 ${noTopMargin ? 'mt-0' : 'mt-2'}`}
      >
        <Container className="d-flex flex-nowrap align-items-center justify-content-between gap-2 py-0">
          <span
            className="text-white-50 mb-0 text-truncate"
            style={{ fontSize: '0.65rem', minWidth: 0 }}
          >
            © {new Date().getFullYear()} {brand}
          </span>
          <nav className="d-flex gap-2 flex-shrink-0" style={{ fontSize: '0.65rem' }}>
            <Link to="/privacy" className="text-white-50 text-decoration-none">
              {t('privacy.title', { defaultValue: 'プライバシー' })}
            </Link>
            <Link to="/terms" className="text-white-50 text-decoration-none">
              {t('terms.title', { defaultValue: '規約' })}
            </Link>
          </nav>
        </Container>
      </footer>
    );
  }

  return (
    <footer className={`bg-dark text-white py-4 ${noTopMargin ? 'mt-0 flex-shrink-0' : 'mt-5'}`}>
      <Container>
        <Row>
          <Col md={6}>
            <h5 className="mb-3">{brand}</h5>
            {companyInfo?.description && (
              <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                {companyInfo.description}
              </p>
            )}
            {companyInfo && (
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                {companyInfo.postalCode && <div>〒{companyInfo.postalCode}</div>}
                {companyInfo.prefecture && companyInfo.city && (
                  <div>
                    {companyInfo.prefecture}
                    {companyInfo.city}
                    {companyInfo.addressLine1 && ` ${companyInfo.addressLine1}`}
                    {companyInfo.addressLine2 && ` ${companyInfo.addressLine2}`}
                  </div>
                )}
                {companyInfo.phone && <div>TEL: {companyInfo.phone}</div>}
                {companyInfo.email && <div>Email: {companyInfo.email}</div>}
                {companyInfo.website && (
                  <div>
                    <a
                      href={companyInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-decoration-none"
                    >
                      {companyInfo.website}
                    </a>
                  </div>
                )}
              </div>
            )}
          </Col>
          <Col md={6} className="text-md-end">
            <ul className="list-unstyled mb-2">
              <li>
                <Link to="/help" className="text-white text-decoration-none">
                  {t('help.title', { defaultValue: 'ヘルプ' })}
                </Link>
              </li>
              <li>
                <Link
                  to={gaisukePublicPath(i18n.language)}
                  className="text-white text-decoration-none"
                >
                  {t('navigation.gaisuke', { defaultValue: 'AIトレードアリーナ' })}
                </Link>
              </li>
              <li>
                <Link
                  to={gaisukeFxPublicPath(i18n.language)}
                  className="text-white text-decoration-none"
                >
                  {t('navigation.gaisuke_fx', {
                    defaultValue: 'AIトレードアリーナ（FXドル円編）',
                  })}
                </Link>
              </li>
              {laboSukePublic ? (
                <li>
                  <Link
                    to={laboSukePublicPath(i18n.language)}
                    className="text-white text-decoration-none"
                  >
                    {t('navigation.labo_suke')}
                  </Link>
                </li>
              ) : null}
            </ul>
            <ul className="list-unstyled mb-0">
              <li>
                <Link to="/contact" className="text-white text-decoration-none">
                  {t('home.nav_contact_private', { defaultValue: 'お問い合わせ（個別）' })}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-white text-decoration-none">
                  {t('privacy.title', { defaultValue: 'プライバシーポリシー' })}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-white text-decoration-none">
                  {t('terms.title', { defaultValue: '利用規約' })}
                </Link>
              </li>
            </ul>
            <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
              <small>
                {t('home.footer_copyright', {
                  year: new Date().getFullYear(),
                  company: brand,
                })}
              </small>
            </p>
          </Col>
        </Row>
      </Container>
    </footer>
  );
}
